import { useFormUiContext } from '@elementary/common-ui/components/AiChat';
import { UseFormReturn } from 'react-hook-form';
import useSelectedWarehouseType from 'shared/hooks/useSelectedWarehouseType';
import { AddTestForm } from 'shared/types';
import {
  EditTestMetadata,
  WizardMode,
  WizardResourceType,
  WizardSelectableTest,
  WizardStageInfo,
} from 'testManagement/types';
import {
  getStageValidationError,
  getWizardStage,
  isStageValid,
} from 'testManagement/utils/utils';
import { getWizardTimestampFieldOptions } from 'testManagement/utils/wizardTimestampFields';
import { WIZARD_UI_CONTEXT_NAME, WIZARD_UI_CONTEXT_PROMPT } from './constants';
import { getWizardFormFields } from './utils';

type Props = {
  methods: UseFormReturn<AddTestForm>;
  stage: number;
  wizardStages: WizardStageInfo[];
  wizardMode: WizardMode;
  wizardResourceType: WizardResourceType;
  editTestId?: string;
  editTestMetadata?: EditTestMetadata;
  selectableTests: WizardSelectableTest[];
  selectableResourceIds?: string[];
  isConfigOnlyFlow: boolean;
  isTestConfigEdited: boolean;
  isTicketRequired: boolean;
  timestampColumns: string[];
};

const useWizardUiContext = ({
  methods,
  stage,
  wizardStages,
  wizardMode,
  wizardResourceType,
  editTestId,
  editTestMetadata,
  selectableTests,
  selectableResourceIds,
  isConfigOnlyFlow,
  isTestConfigEdited,
  isTicketRequired,
  timestampColumns,
}: Props) => {
  const currentWizardStage = getWizardStage(wizardStages, stage);
  const warehouseType = useSelectedWarehouseType();

  useFormUiContext({
    name: WIZARD_UI_CONTEXT_NAME,
    prompt: WIZARD_UI_CONTEXT_PROMPT,
    methods,
    isEnabled: true,
    fields: getWizardFormFields({
      stage,
      wizardStages,
      wizardMode,
      wizardResourceType,
      selectableTests,
      selectableResourceIds,
      timestampColumns,
      warehouseType,
    }),
    getState: () => {
      const { resources, testType, yamlTemplateName } = methods.getValues();
      return {
        wizardMode,
        wizardResourceType,
        stage: currentWizardStage,
        stages: wizardStages,
        stageValid: isStageValid(
          methods,
          stage,
          isConfigOnlyFlow,
          isTestConfigEdited,
          isTicketRequired,
        ),
        stageError: getStageValidationError(
          methods,
          stage,
          isConfigOnlyFlow,
          timestampColumns,
        ),
        hint: currentWizardStage.hint,
        editTestId: editTestId ?? null,
        editedTestMetadata: editTestMetadata ?? null,
        selection: {
          resources: resources ?? [],
          testType: testType ?? '',
          yamlTemplateName: yamlTemplateName ?? '',
        },
        testTemplates: selectableTests,
        timestampFieldOptions: getWizardTimestampFieldOptions(
          testType,
          timestampColumns,
        ),
      };
    },
  });
};

export default useWizardUiContext;
